// Port of Flutter's VelocityTracker (gestures/velocity_tracker.dart): a
// quadratic least-squares fit over the newest samples that form continuous
// motion (≤ 100 ms old, no gap over 40 ms), evaluated at the newest sample.
// A pointer that has not moved for 40 ms before release has velocity 0.

const HORIZON_MS = 100;
const ASSUME_STOPPED_MS = 40;
const HISTORY_SIZE = 20;
const MIN_SAMPLES = 3;
const PRECISION = 1e-10;

interface Sample {
  t: number;
  v: number;
}

/** Slope at t = 0 of the least-squares parabola through (t, v), or null when degenerate. */
function quadraticSlopeAtZero(times: readonly number[], values: readonly number[]): number | null {
  // Normal equations for v ≈ a + b·t + c·t².
  const s = [0, 0, 0, 0, 0];
  const r = [0, 0, 0];
  for (let i = 0; i < times.length; i++) {
    const t = times[i] ?? 0;
    const v = values[i] ?? 0;
    let power = 1;
    for (let k = 0; k < 5; k++) {
      s[k] = (s[k] ?? 0) + power;
      if (k < 3) r[k] = (r[k] ?? 0) + power * v;
      power *= t;
    }
  }
  const at = (k: number) => s[k] ?? 0;
  const m = [
    [at(0), at(1), at(2), r[0] ?? 0],
    [at(1), at(2), at(3), r[1] ?? 0],
    [at(2), at(3), at(4), r[2] ?? 0],
  ];
  // Gaussian elimination with partial pivoting.
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let row = col + 1; row < 3; row++) {
      if (Math.abs(m[row]?.[col] ?? 0) > Math.abs(m[pivot]?.[col] ?? 0)) pivot = row;
    }
    const pivotRow = m[pivot];
    const currentRow = m[col];
    if (!pivotRow || !currentRow || Math.abs(pivotRow[col] ?? 0) < PRECISION) return null;
    m[pivot] = currentRow;
    m[col] = pivotRow;
    for (let row = col + 1; row < 3; row++) {
      const target = m[row];
      if (!target) continue;
      const factor = (target[col] ?? 0) / (pivotRow[col] ?? 1);
      for (let k = col; k < 4; k++) target[k] = (target[k] ?? 0) - factor * (pivotRow[k] ?? 0);
    }
  }
  const coefficients = [0, 0, 0];
  for (let row = 2; row >= 0; row--) {
    const line = m[row];
    if (!line) return null;
    let sum = line[3] ?? 0;
    for (let k = row + 1; k < 3; k++) sum -= (line[k] ?? 0) * (coefficients[k] ?? 0);
    coefficients[row] = sum / (line[row] ?? 1);
  }
  const slope = coefficients[1] ?? 0;
  return Number.isFinite(slope) ? slope : null;
}

export class VelocityTracker {
  private samples: Sample[] = [];

  reset(): void {
    this.samples = [];
  }

  add(timeMs: number, value: number): void {
    this.samples.push({ t: timeMs, v: value });
    if (this.samples.length > HISTORY_SIZE) this.samples.shift();
  }

  /**
   * Units per second (px/s for positions in px) at `nowMs`, the release time
   * in the same time base as `add()`. 0 when the pointer stopped moving more
   * than 40 ms earlier or fewer than three samples describe the motion.
   */
  velocity(nowMs: number): number {
    const newest = this.samples[this.samples.length - 1];
    if (!newest || nowMs - newest.t > ASSUME_STOPPED_MS) return 0;
    const times: number[] = [];
    const values: number[] = [];
    let previous = newest;
    for (let i = this.samples.length - 1; i >= 0; i--) {
      const sample = this.samples[i];
      if (!sample) break;
      const age = newest.t - sample.t;
      const gap = Math.abs(previous.t - sample.t);
      previous = sample;
      if (age > HORIZON_MS || gap > ASSUME_STOPPED_MS) break;
      times.push(-age);
      values.push(sample.v);
    }
    if (times.length < MIN_SAMPLES) return 0;
    const slope = quadraticSlopeAtZero(times, values);
    return slope === null ? 0 : slope * 1000;
  }
}
